import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '../../stores/organization.store';
import { getUsers } from '../../services/auth.service';
import {
  getOrganizationTrainingSummary,
  listEmployeeTrainingSessions,
  normalizeTrainingSessionsResult,
  normalizeTrainingSummaryResult,
} from '../../services/employee-training.service';
import type { UserSchema } from '../../types/auth.type';
import type { EmployeeTrainingRecord, GenericResponse } from '../../types/employee-training.type';
import PageHeader from '../../components/PageHeader';
import RecordTrainingDialog from '../../components/employee-training/RecordTrainingDialog';
import TrainingTableToolbar from '../../components/employee-training/TrainingTableToolbar';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import {
  HELP_GUIDE_TRAINING_DETAIL,
  HELP_GUIDE_TRAINING_OVERVIEW,
} from '../../constants/knowledgeBaseTryIt';
import { helpI18n } from '../../i18n/help';
import {
  clearHelpGuideActive,
  getActiveHelpGuide,
  getTrainingDetailHelpPhase,
  getTrainingOverviewHelpPhase,
  isOverviewDetailPhase,
  markHelpGuideActive,
  setTrainingDetailHelpPhase,
  setTrainingOverviewHelpPhase,
  type TrainingDetailHelpPhase,
} from '../../utils/helpGuideSession';
import {
  cellOrDash,
  employeeDisplayName,
  enrichTrainingSummaryList,
  EMPTY_CELL,
  isPlaceholderCell,
  organizationUsersQueryKey,
} from '../../utils/employee-training.util';
import { trainingBodyCellSx } from '../../components/employee-training/trainingTable.styles';

type Order = 'asc' | 'desc';

type SessionSortKey =
  | 'session_date'
  | 'hours'
  | 'title'
  | 'notes'
  | 'requirement'
  | 'recorded_by'
  | 'created_at';

const SESSIONS_TABLE_COL_COUNT = 8;

const sessionCell = {
  text: { align: 'left' as const },
  ltr: {
    align: 'left' as const,
    sx: { direction: 'ltr', unicodeBidi: 'plaintext', whiteSpace: 'nowrap' },
  },
  date: {
    align: 'left' as const,
    sx: { direction: 'ltr', unicodeBidi: 'plaintext', whiteSpace: 'nowrap' },
  },
  numeric: {
    align: 'right' as const,
    sx: { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  },
  wrap: { align: 'left' as const, sx: { maxWidth: 200 } },
  mono: {
    align: 'left' as const,
    sx: { direction: 'ltr', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' },
  },
};

function truncateNotes(notes: string | null, max = 48): string {
  if (!notes) return '';
  const t = notes.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

function sessionRequirementLabel(row: EmployeeTrainingRecord): string {
  const data = row.data ?? {};
  return [data.requirement_code, data.training_category].filter(Boolean).join(' · ');
}

function sessionSearchHay(row: EmployeeTrainingRecord): string {
  const data = row.data ?? {};
  return [
    row.session_date,
    String(row.hours),
    row.title ?? '',
    row.notes ?? '',
    data.requirement_code ?? '',
    data.training_category ?? '',
    typeof data.external_reference_url === 'string' ? data.external_reference_url : '',
    row.recorded_by_user_id ?? '',
    row.created_at,
  ]
    .join(' ')
    .toLowerCase();
}

function compareSessions(a: EmployeeTrainingRecord, b: EmployeeTrainingRecord, orderBy: SessionSortKey): number {
  switch (orderBy) {
    case 'session_date':
      return a.session_date.localeCompare(b.session_date);
    case 'hours':
      return a.hours - b.hours;
    case 'title':
      return (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' });
    case 'notes':
      return (a.notes ?? '').localeCompare(b.notes ?? '', undefined, { sensitivity: 'base' });
    case 'requirement':
      return sessionRequirementLabel(a).localeCompare(sessionRequirementLabel(b), undefined, {
        sensitivity: 'base',
      });
    case 'recorded_by':
      return (a.recorded_by_user_id ?? '').localeCompare(b.recorded_by_user_id ?? '');
    case 'created_at':
      return a.created_at.localeCompare(b.created_at);
    default:
      return 0;
  }
}

const EmployeeTrainingDetail: React.FC = () => {
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const theme = useTheme();
  const navigate = useNavigate();
  const { userId = '' } = useParams<{ userId: string }>();
  const location = useLocation() as { state?: { employeeLabel?: string } };
  const { selectedOrganization } = useOrganizationStore();
  const organizationId = selectedOrganization?.id ?? '';

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [recordOpen, setRecordOpen] = useState(false);

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SessionSortKey>('session_date');
  const [filterText, setFilterText] = useState('');
  const [detailHelpPhase, setDetailHelpPhase] = useState<TrainingDetailHelpPhase | null>(
    () => {
      const active = getActiveHelpGuide();
      if (active === HELP_GUIDE_TRAINING_OVERVIEW) {
        const overviewPhase = getTrainingOverviewHelpPhase();
        if (overviewPhase === 'detailSummary') return 'summary';
        if (overviewPhase === 'detailFilter') return 'filter';
        if (overviewPhase === 'detailTable') return 'table';
        return null;
      }
      if (active !== HELP_GUIDE_TRAINING_DETAIL) return null;
      const phase = getTrainingDetailHelpPhase();
      return phase === 'view' ? 'summary' : phase;
    },
  );

  const showSummaryTip = detailHelpPhase === 'summary';
  const showFilterTip = detailHelpPhase === 'filter';
  const showTableTip = detailHelpPhase === 'table';

  const dismissDetailHelpGuide = () => {
    setDetailHelpPhase(null);
    clearHelpGuideActive();
  };

  const finishDetailHelpGuide = () => {
    setDetailHelpPhase(null);
    if (getActiveHelpGuide() === HELP_GUIDE_TRAINING_OVERVIEW) {
      setTrainingOverviewHelpPhase('addTraining');
      markHelpGuideActive(HELP_GUIDE_TRAINING_OVERVIEW);
      navigate('/employee-training');
      return;
    }
    clearHelpGuideActive();
  };

  const advanceDetailHelpGuide = () => {
    if (detailHelpPhase === 'summary') {
      if (getActiveHelpGuide() === HELP_GUIDE_TRAINING_OVERVIEW) {
        setTrainingOverviewHelpPhase('detailFilter');
      } else {
        setTrainingDetailHelpPhase('filter');
      }
      setDetailHelpPhase('filter');
      return;
    }
    if (detailHelpPhase === 'filter') {
      if (getActiveHelpGuide() === HELP_GUIDE_TRAINING_OVERVIEW) {
        setTrainingOverviewHelpPhase('detailTable');
      } else {
        setTrainingDetailHelpPhase('table');
      }
      setDetailHelpPhase('table');
      return;
    }
    finishDetailHelpGuide();
  };

  useEffect(() => {
    const active = getActiveHelpGuide();
    if (active === HELP_GUIDE_TRAINING_OVERVIEW) {
      const overviewPhase = getTrainingOverviewHelpPhase();
      if (isOverviewDetailPhase(overviewPhase)) {
        if (overviewPhase === 'detailSummary') setDetailHelpPhase('summary');
        if (overviewPhase === 'detailFilter') setDetailHelpPhase('filter');
        if (overviewPhase === 'detailTable') setDetailHelpPhase('table');
      }
      return;
    }
    if (active !== HELP_GUIDE_TRAINING_DETAIL) return;
    const phase = getTrainingDetailHelpPhase();
    if (phase === 'summary' || phase === 'filter' || phase === 'table') {
      setDetailHelpPhase(phase);
    } else if (phase === 'view') {
      setTrainingDetailHelpPhase('summary');
      setDetailHelpPhase('summary');
    }
  }, [userId]);

  useEffect(() => {
    if (!detailHelpPhase) return;
    const id =
      detailHelpPhase === 'table'
        ? 'help-training-detail-table'
        : detailHelpPhase === 'filter'
          ? 'help-training-detail-filter'
          : 'help-training-detail-summary';
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [detailHelpPhase]);

  useEffect(() => {
    setPage(0);
  }, [userId]);

  useEffect(() => {
    setFilterText('');
  }, [page, rowsPerPage, userId]);

  const trainingQueryRetry = (failureCount: number, err: unknown) => {
    const status = (err as AxiosError).response?.status;
    if (status === 403 || status === 404) return false;
    return failureCount < 2;
  };

  const summaryQuery = useQuery({
    queryKey: ['training-summary', organizationId],
    queryFn: async () => {
      const res = await getOrganizationTrainingSummary(organizationId);
      return normalizeTrainingSummaryResult(res);
    },
    enabled: Boolean(organizationId),
    retry: trainingQueryRetry,
  });

  const usersQuery = useQuery({
    queryKey: organizationUsersQueryKey(organizationId),
    queryFn: async () => {
      const res = (await getUsers(organizationId)) as GenericResponse<UserSchema[]>;
      return res.result ?? [];
    },
    enabled: Boolean(organizationId),
    staleTime: 5 * 60 * 1000,
    retry: trainingQueryRetry,
  });

  const sessionsQuery = useQuery({
    queryKey: ['training-sessions', organizationId, userId, page, rowsPerPage],
    queryFn: async () => {
      const res = await listEmployeeTrainingSessions(organizationId, userId, {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      return normalizeTrainingSessionsResult(res);
    },
    enabled: Boolean(organizationId && userId),
    retry: (failureCount, err) => {
      const status = (err as AxiosError).response?.status;
      if (status === 403 || status === 404) return false;
      return failureCount < 2;
    },
  });

  const enrichedSummaryRows = useMemo(
    () => enrichTrainingSummaryList(summaryQuery.data?.result_list ?? [], usersQuery.data ?? []),
    [summaryQuery.data, usersQuery.data],
  );

  const employeeLabel = useMemo(() => {
    const fromNav = location.state?.employeeLabel;
    if (fromNav) return fromNav;
    const row = enrichedSummaryRows.find((r) => r.user_id === userId);
    if (row) return employeeDisplayName(row);
    return userId;
  }, [location.state, enrichedSummaryRows, userId]);

  const forbidden = (sessionsQuery.error as AxiosError | undefined)?.response?.status === 403;
  const notFound = (sessionsQuery.error as AxiosError | undefined)?.response?.status === 404;

  const list = sessionsQuery.data?.result_list ?? [];
  const totalCount = sessionsQuery.data?.total_count ?? 0;
  const totalHours = sessionsQuery.data?.total_hours ?? 0;

  const filteredSortedList = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    let data = [...list];
    if (q) {
      data = data.filter((row) => sessionSearchHay(row).includes(q));
    }
    const mult = order === 'asc' ? 1 : -1;
    data.sort((a, b) => mult * compareSessions(a, b, orderBy));
    return data;
  }, [list, filterText, order, orderBy]);

  const handleRequestSort = (property: SessionSortKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  if (!selectedOrganization) {
    return (
      <Box>
        <Alert severity="info">{t('employeeTraining.detail.selectOrganization')}</Alert>
      </Box>
    );
  }

  if (!userId) {
    return (
      <Box>
        <Alert severity="error">{t('employeeTraining.detail.missingEmployee')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        icon={<SchoolOutlinedIcon />}
        title={t('employeeTraining.detail.title')}
        subtitle={employeeLabel}
        onBack={() => navigate('/employee-training')}
        backLabel={t('employeeTraining.back')}
        actions={
          <Button
            variant="contained"
            onClick={() => setRecordOpen(true)}
            disabled={forbidden || notFound}
          >
            {t('employeeTraining.addTraining')}
          </Button>
        }
      />

      {(sessionsQuery.isFetching || summaryQuery.isFetching) && <LinearProgress sx={{ mb: 2 }} />}

      {forbidden ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('employeeTraining.detail.forbidden')}
        </Alert>
      ) : null}

      {notFound ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(sessionsQuery.error as AxiosError<{ detail?: string }>)?.response?.data?.detail ||
            t('employeeTraining.detail.notFound')}
        </Alert>
      ) : null}

      {!forbidden && !notFound && sessionsQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(sessionsQuery.error as AxiosError<{ detail?: string }>)?.response?.data?.detail ||
            (sessionsQuery.error as Error).message ||
            t('employeeTraining.detail.loadError')}
        </Alert>
      ) : null}

      {!forbidden && !notFound && !sessionsQuery.isError ? (
        <>
          <SetupCoachmark
            open={showSummaryTip}
            title={tHelp('knowledgeBase.trainingDetailSummaryTitle')}
            subtitle={tHelp('knowledgeBase.trainingDetailSummarySubtitle')}
            onDismiss={dismissDetailHelpGuide}
            onNext={advanceDetailHelpGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
            placement="bottom"
          >
            <Paper
              id="help-training-detail-summary"
              sx={{ p: 2, mb: 2, boxShadow: theme.shadows[2] }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('employeeTraining.detail.totalHours')}:{' '}
                <Typography component="span" variant="body1" fontWeight={600} color="text.primary">
                  {Number(totalHours).toFixed(2)}
                </Typography>
                {' · '}
                {t('employeeTraining.detail.sessionCount')}:{' '}
                <Typography component="span" variant="body1" fontWeight={600} color="text.primary">
                  {totalCount}
                </Typography>
              </Typography>
            </Paper>
          </SetupCoachmark>

          <SetupCoachmark
            open={showFilterTip}
            title={tHelp('knowledgeBase.trainingDetailFilterTitle')}
            subtitle={tHelp('knowledgeBase.trainingDetailFilterSubtitle')}
            onDismiss={dismissDetailHelpGuide}
            onNext={advanceDetailHelpGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
            placement="bottom"
          >
            <Box id="help-training-detail-filter">
              <TrainingTableToolbar
                filterText={filterText}
                onFilterTextChange={setFilterText}
              />
            </Box>
          </SetupCoachmark>
          <SetupCoachmark
            open={showTableTip}
            title={tHelp('knowledgeBase.trainingDetailTableTitle')}
            subtitle={tHelp('knowledgeBase.trainingDetailTableSubtitle')}
            onDismiss={dismissDetailHelpGuide}
            onNext={finishDetailHelpGuide}
            nextLabel={
              getActiveHelpGuide() === HELP_GUIDE_TRAINING_OVERVIEW
                ? t('selfSetup.tourNext')
                : t('selfSetup.tourDone')
            }
            fullWidth
            placement="top"
          >
            <Paper
              id="help-training-detail-table"
              sx={{ width: '100%', overflow: 'hidden' }}
            >
            <TableContainer sx={{ maxHeight: { md: 'calc(100vh - 340px)' } }}>
              <Table
                stickyHeader
                aria-label={t('employeeTraining.tableAria.sessions')}
                sx={{ minWidth: 960 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      align={sessionCell.date.align}
                      sortDirection={orderBy === 'session_date' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'session_date'}
                        direction={orderBy === 'session_date' ? order : 'asc'}
                        onClick={() => handleRequestSort('session_date')}
                      >
                        {t('employeeTraining.detail.columns.date')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align={sessionCell.numeric.align}
                      sortDirection={orderBy === 'hours' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'hours'}
                        direction={orderBy === 'hours' ? order : 'asc'}
                        onClick={() => handleRequestSort('hours')}
                      >
                        {t('employeeTraining.detail.columns.hours')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align={sessionCell.text.align}
                      sortDirection={orderBy === 'title' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'title'}
                        direction={orderBy === 'title' ? order : 'asc'}
                        onClick={() => handleRequestSort('title')}
                      >
                        {t('employeeTraining.detail.columns.title')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align={sessionCell.text.align}
                      sortDirection={orderBy === 'notes' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'notes'}
                        direction={orderBy === 'notes' ? order : 'asc'}
                        onClick={() => handleRequestSort('notes')}
                      >
                        {t('employeeTraining.detail.columns.notes')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align={sessionCell.text.align}
                      sortDirection={orderBy === 'requirement' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'requirement'}
                        direction={orderBy === 'requirement' ? order : 'asc'}
                        onClick={() => handleRequestSort('requirement')}
                      >
                        {t('employeeTraining.detail.columns.requirement')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align={sessionCell.text.align}>
                      {t('employeeTraining.detail.columns.link')}
                    </TableCell>
                    <TableCell
                      align={sessionCell.mono.align}
                      sortDirection={orderBy === 'recorded_by' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'recorded_by'}
                        direction={orderBy === 'recorded_by' ? order : 'asc'}
                        onClick={() => handleRequestSort('recorded_by')}
                      >
                        {t('employeeTraining.detail.columns.recordedBy')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align={sessionCell.date.align}
                      sortDirection={orderBy === 'created_at' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'created_at'}
                        direction={orderBy === 'created_at' ? order : 'asc'}
                        onClick={() => handleRequestSort('created_at')}
                      >
                        {t('employeeTraining.detail.columns.created')}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.length === 0 && !sessionsQuery.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={SESSIONS_TABLE_COL_COUNT}>
                        <Typography color="text.secondary" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
                          {t('employeeTraining.detail.noSessions')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {list.length > 0 && filteredSortedList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={SESSIONS_TABLE_COL_COUNT}>
                        <Typography color="text.secondary" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
                          {t('employeeTraining.detail.noFilterMatch')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {filteredSortedList.map((row) => {
                    const data = row.data ?? {};
                    const reqLabel = sessionRequirementLabel(row);
                    const requirement = reqLabel ? reqLabel : EMPTY_CELL;
                    const url =
                      typeof data.external_reference_url === 'string' ? data.external_reference_url.trim() : '';
                    const notesFull = row.notes?.trim() || '';
                    const notesShort = truncateNotes(row.notes);
                    const notesDisplay = notesFull ? notesShort : EMPTY_CELL;
                    const title = cellOrDash(row.title);
                    const recordedBy = cellOrDash(row.recorded_by_user_id);
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell
                          align={sessionCell.date.align}
                          sx={trainingBodyCellSx(false, sessionCell.date.sx)}
                        >
                          {row.session_date}
                        </TableCell>
                        <TableCell
                          align={sessionCell.numeric.align}
                          sx={trainingBodyCellSx(false, sessionCell.numeric.sx)}
                        >
                          {Number(row.hours).toFixed(2)}
                        </TableCell>
                        <TableCell
                          align={sessionCell.text.align}
                          sx={trainingBodyCellSx(isPlaceholderCell(title))}
                        >
                          {title}
                        </TableCell>
                        <TableCell
                          align={sessionCell.text.align}
                          sx={trainingBodyCellSx(isPlaceholderCell(notesDisplay), sessionCell.wrap.sx)}
                        >
                          {notesFull ? (
                            <Tooltip title={notesFull}>
                              <span>{notesShort}</span>
                            </Tooltip>
                          ) : (
                            EMPTY_CELL
                          )}
                        </TableCell>
                        <TableCell
                          align={sessionCell.text.align}
                          sx={trainingBodyCellSx(isPlaceholderCell(requirement), { maxWidth: 160 })}
                        >
                          {requirement}
                        </TableCell>
                        <TableCell
                          align={sessionCell.text.align}
                          sx={trainingBodyCellSx(!url)}
                        >
                          {url ? (
                            <MuiLink href={url} target="_blank" rel="noopener noreferrer" sx={{ direction: 'ltr' }}>
                              {t('employeeTraining.detail.openLink')}
                            </MuiLink>
                          ) : (
                            EMPTY_CELL
                          )}
                        </TableCell>
                        <TableCell
                          align={sessionCell.mono.align}
                          sx={trainingBodyCellSx(isPlaceholderCell(recordedBy), sessionCell.mono.sx)}
                        >
                          {recordedBy}
                        </TableCell>
                        <TableCell
                          align={sessionCell.date.align}
                          sx={trainingBodyCellSx(false, sessionCell.date.sx)}
                        >
                          {row.created_at}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t('common.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => t('common.rangeOf', { from, to, count })}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
          </SetupCoachmark>
        </>
      ) : null}

      <RecordTrainingDialog
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        organizationId={organizationId}
        userId={userId}
        employeeLabel={employeeLabel}
      />
    </Box>
  );
};

export default EmployeeTrainingDetail;
